import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { GridSelectionModel } from '@mui/x-data-grid';
import { useQuery } from 'react-query';

import { mergeArrays, removeDuplicates } from 'helpers/utils';

import type { Props, ParamsToSearch } from './types';

/**
 * Custom React hook for managing selected items in a table for export to Excel, PDF and CSV.
 *
 * @template T - The type of items in the table
 * @param {Object} props - The props object containing configuration parameters.
 * @param {Function} props.getFunction - The function to fetch additional items based on search criteria.
 * @param {keyof T} props.idName - The name of the property serving as the unique identifier for items.
 * @param {boolean} [props.isRefetch] - Boolean indicating whether a refetch is needed.
 * @param {T[]} [props.items=[]] - The array of items currently displayed in the table.
 * @param {number} [props.pageNumber] - The current page number of the table.
 * @param {number} [props.pageSize] - The number of items per page in the table.
 * @param {boolean} [props.hasSearch] - Boolean indicating whether a refetch when a searching is execute.
 */
function useSelectTableItemsToExport<T>({
  getFunction,
  idName,
  isRefetch,
  items = [],
  pageNumber,
  pageSize,
  hasSearch = false,
  exportFormatter = null
}: Props<T>) {
  // State variables for selected items and visited pages
  const [itemsSelectedForExport, setItemsSelectedForExport] = useState<T[]>([]);
  const [pagesVisited, setPagesVisited] = useState([0]);

  // Reference to hold all fetched items
  const allItemsFetched = useRef<T[]>(items);

  // State variables for managing data merging and search criteria
  const [needMergeData, setNeedMergeData] = useState(false);
  const [paramsToSearch, setParamsToSearch] = useState<ParamsToSearch>(null);

  const itemsSelectedForExportMemo = useMemo(() => {
    if (exportFormatter) {
      return itemsSelectedForExport?.map((item) => exportFormatter(item));
    }
    return itemsSelectedForExport;
  }, [exportFormatter, itemsSelectedForExport]);

  // Effect to trigger data merging based on pagination or refetching
  useEffect(() => {
    if (isRefetch || pageSize !== 25 || hasSearch) {
      setNeedMergeData(true);
    }
  }, [isRefetch, pageSize, items, hasSearch]);

  useEffect(() => {
    if (needMergeData && !isRefetch && items?.length !== 0) {
      allItemsFetched.current = mergeArrays(
        allItemsFetched?.current || [],
        items,
        idName
      );

      setNeedMergeData(false);
    }
  }, [idName, isRefetch, items, needMergeData, pageSize]);

  const onUpdateItem = useCallback(
    ([newItem]: T[]) => {
      setItemsSelectedForExport((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem[idName] !== newItem[idName] ? prevItem : newItem
        )
      );

      setParamsToSearch(null);

      allItemsFetched.current = allItemsFetched.current.map((item) =>
        item[idName] !== newItem[idName] ? item : newItem
      );
    },
    [idName]
  );

  // Query to fetch additional items based on search criteria
  useQuery([paramsToSearch], () => getFunction(paramsToSearch), {
    select: ({ data }) => data?.data?.items,
    enabled: !!paramsToSearch,
    refetchOnWindowFocus: false,
    onSuccess: onUpdateItem
  });

  // Effect to initialize allItemsFetched with current items
  useEffect(() => {
    if (items?.length !== 0 && allItemsFetched.current?.length === 0) {
      allItemsFetched.current = items;
    }
  }, [items]);

  // Effect to merge items when pagination changes
  useEffect(() => {
    if (
      items?.length !== 0 &&
      pageNumber !== 0 &&
      !pagesVisited.includes(pageNumber)
    ) {
      allItemsFetched.current = removeDuplicates(
        [...allItemsFetched.current, ...items],
        idName
      );
      setPagesVisited((prevPagesVisited) => [...prevPagesVisited, pageNumber]);
    }
  }, [items, pagesVisited, pageNumber, idName]);

  /**
   * Handles selecting items for download based on their IDs.
   *
   * @param {GridSelectionModel} ids - The IDs of the items selected for download
   */
  const handleSelectItemForExport = useCallback(
    (ids: GridSelectionModel) => {
      if (allItemsFetched.current.length === 0) {
        allItemsFetched.current = items;
      }

      const itemsSelected = allItemsFetched.current.filter((item) =>
        ids.includes(item[idName] as string | number)
      );

      setItemsSelectedForExport(itemsSelected);
    },
    [idName, items]
  );

  /**
   * Resets the selected items for export.
   */
  const handleResetItemsForExport = useCallback(() => {
    allItemsFetched.current = [];
    setItemsSelectedForExport([]);
  }, []);

  /**
   * Handles editing an item by setting the search criteria.
   *
   * @param {ParamsToSearch} params - The params of the item being edited
   */
  const handleEditItemForExport = useCallback((params: object) => {
    setParamsToSearch(params);
  }, []);

  /**
   * Handles deleting an item from the selected items for export.
   *
   * @param {T[keyof T]} id - The unique identifier of the item to be deleted
   */
  const handleDeleteItemForExport = useCallback(
    (id: T[keyof T]) => {
      setItemsSelectedForExport((prevItems) =>
        prevItems.filter((item) => item[idName] !== id)
      );

      allItemsFetched.current = allItemsFetched.current.filter(
        (item) => item[idName] !== id
      );
    },
    [idName]
  );

  /**
   * Handles deleting selected items for export based on an array of IDs.
   * @param {keyof T[]} ids - The uniques identifiers of the items to be deleted
   */
  const handleDeleteItemsForExport = useCallback(
    (ids: T[keyof T][]) => {
      setItemsSelectedForExport((prevItems) =>
        prevItems.filter((item) => !ids.includes(item[idName]))
      );

      allItemsFetched.current = allItemsFetched.current.filter(
        (item) => !ids.includes(item[idName])
      );
    },
    [idName]
  );

  return {
    handleDeleteItemForExport,
    handleDeleteItemsForExport,
    handleEditItemForExport,
    handleResetItemsForExport,
    handleSelectItemForExport,
    itemsSelectedForExport: itemsSelectedForExportMemo
  };
}

export { useSelectTableItemsToExport };
